// This module is consumed by frontend clients through the `./utils`
// entrypoint, so it must stay free of runtime imports — keep every type local.

type CoverPhotoFileType =
  | 'ORG_PROPOSAL_HEADER'
  | 'USER_UPLOAD'
  | 'AIRSHIP_DEFAULT'
  | 'NO_IMAGE';

export type ResolvableCoverPhoto = {
  fileType: CoverPhotoFileType;
  fileId?: string;
};

export type ResolvedCoverPhoto = {
  fileType: CoverPhotoFileType;
  fileId?: string;
  url?: string;
};

export type ResolvableOrgHeaderImage = {
  proposalHeaderImageId: string;
  isDefault: boolean;
  image?: { url: string };
};

const coverImageBlue = 'b099f60b-cf3b-4f9b-ba61-ff6e33e8742a';
const coverImageGold = 'fcd9b6aa-cb3a-4e85-8af9-4396d92a16e8';
const coverImageRed = 'fef233e0-2616-4e0a-8c74-d75bab655e3e';

export const DEFAULT_COVER_IMAGE_IDS: readonly string[] = [
  coverImageBlue,
  coverImageGold,
  coverImageRed,
];

export const getDefaultCoverImageId = (theme?: string): string => {
  switch (theme) {
    case 'AIRSHIP_GOLD':
      return coverImageGold;
    case 'AIRSHIP_RED':
      return coverImageRed;
    default:
      return coverImageBlue;
  }
};

export type CoverPhotoImageFormat = 'webp' | 'jpeg' | 'png';

export const coverPhotoAssetPath = (fileId: string): string[] => [
  'airship-proposal-assets',
  `header-img-${fileId}.png`,
];

// The public-files CDN routes `transforms/*` requests through its image
// optimizer; the raw path serves the stored full-size PNG untouched.
export const coverPhotoTransformAssetPath = (fileId: string): string[] => [
  'transforms',
  ...coverPhotoAssetPath(fileId),
];

export const coverPhotoTransformQuery = (
  format: CoverPhotoImageFormat = 'webp'
): string => `w=1600&fm=${format}`;

export const buildCoverPhotoUrl = (
  baseUrl: string,
  fileId: string,
  format: CoverPhotoImageFormat = 'webp'
): string =>
  `${[baseUrl, ...coverPhotoTransformAssetPath(fileId)].join(
    '/'
  )}?${coverPhotoTransformQuery(format)}`;

/**
 * Offline mirror of `ProposalHeaderImageService.getHeaderImage`, with the DAL
 * lookups and CloudFront signing replaced by the org header images synced into
 * the offline org-content document (already signed server-side). The branching
 * must stay result-equivalent to the service — change them together.
 *
 * USER_UPLOAD is not resolvable offline (per-appointment files are not
 * synced), so it degrades to no URL rather than a wrong image.
 */
export const resolveCoverPhoto = ({
  coverPhoto,
  theme,
  orgHeaderImages,
  buildAssetUrl,
}: {
  coverPhoto?: ResolvableCoverPhoto;
  theme?: string;
  orgHeaderImages?: readonly ResolvableOrgHeaderImage[];
  buildAssetUrl: (paths: string[]) => string;
}): ResolvedCoverPhoto => {
  const transformedAssetUrl = (fileId: string): string =>
    `${buildAssetUrl(
      coverPhotoTransformAssetPath(fileId)
    )}?${coverPhotoTransformQuery()}`;

  const themeDefault = (): ResolvedCoverPhoto => {
    const fileId = getDefaultCoverImageId(theme);
    return {
      fileType: 'NO_IMAGE',
      fileId,
      url: transformedAssetUrl(fileId),
    };
  };

  if (!coverPhoto) {
    // First flagged default wins in stored order, mirroring the service's
    // unsorted findByOrganization pick. Offline cannot tell "never synced"
    // from "org has no default", so both fall through to the theme default.
    const defaultImage = orgHeaderImages?.find((image) => image.isDefault);

    if (defaultImage?.image) {
      return {
        fileType: 'ORG_PROPOSAL_HEADER',
        fileId: defaultImage.proposalHeaderImageId,
        url: defaultImage.image.url,
      };
    }

    return themeDefault();
  }

  if (!coverPhoto.fileId) {
    return themeDefault();
  }

  switch (coverPhoto.fileType) {
    case 'ORG_PROPOSAL_HEADER':
      return {
        fileType: coverPhoto.fileType,
        fileId: coverPhoto.fileId,
        url: orgHeaderImages?.find(
          (image) => image.proposalHeaderImageId === coverPhoto.fileId
        )?.image?.url,
      };

    case 'USER_UPLOAD':
      return {
        fileType: coverPhoto.fileType,
        fileId: coverPhoto.fileId,
        url: undefined,
      };

    case 'AIRSHIP_DEFAULT':
      return {
        fileType: coverPhoto.fileType,
        fileId: coverPhoto.fileId,
        url: transformedAssetUrl(coverPhoto.fileId),
      };

    case 'NO_IMAGE':
      return themeDefault();
  }
};
